/* 
    based on https://manuel-rauber.com/2016/01/05/angular-2-spinner-component/
    we didn't want to link with a third party module
*/

import { ChangeDetectionStrategy, Component, computed, effect, input, model, OnDestroy, signal, untracked } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { BarI18nPipe } from '../i18n/bar-i18n.pipe';
import { BaasLoadingOptions } from './baas-loading-options.type';

@Component({
    selector: 'baas-loading',
    templateUrl: './baas-loading.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatCardModule, MatProgressBarModule, BarI18nPipe]
})
export class BaasLoading implements OnDestroy {
    readonly options = model<BaasLoadingOptions>(undefined);
    /* Whether to block the page or work on element */
    readonly blockPage = input(false);
    readonly running = model<boolean>(false);

    readonly fromDirective = signal(false);
    protected readonly delayedRunning = signal(false);

    protected readonly duration = computed(() => {
        return this.options()?.duration || 'short';
    });

    readonly #delay = computed(() => this.options()?.delay || 0);

    #currentTimeout: number = undefined;

    constructor() {
        effect(() => {
            const running = this.running();
            this.#log('Running = ' + running);
            untracked(() => {
                if (this.options()?.enabled === false) {
                    this.#log('Loading disabled; skipping');
                    return;
                }

                if (!running) {
                    this.#cancelTimeout();
                    this.#log('Delayed running false');
                    this.delayedRunning.set(false);
                    return;
                }

                if (this.#currentTimeout !== undefined) {
                    this.#log('Existing timeout, skipping');
                    return;
                }

                this.#log('Creating new timeout');

                this.#currentTimeout = window.setTimeout(() => {
                    this.#log('In timeout, delayed running ' + running);
                    this.delayedRunning.set(running);
                    this.#cancelTimeout();
                }, this.#delay());
            });
        });
    }

    ngOnDestroy(): void {
        this.#cancelTimeout();
    }

    #cancelTimeout(): void {
        if (this.#currentTimeout !== undefined) {
            this.#log('Cancelling timeout');
            window.clearTimeout(this.#currentTimeout);
            this.#currentTimeout = undefined;
        }
    }

    #log(message: string) {
        if (this.options()?.logging === true) {
            console.log(`[BaasLoading] ${message}`);
        }
    }
}
